import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, 
  CheckCircle2, 
  ChevronRight, 
  Zap, 
  Info, 
  AlertTriangle, 
  Loader2 
} from 'lucide-react';

interface CancelSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  cancelStep: 'survey' | 'retention_price' | 'retention_roadmap' | 'confirm';
  setCancelStep: (step: 'survey' | 'retention_price' | 'retention_roadmap' | 'confirm') => void;
  selectedReason: string;
  setSelectedReason: (r: string) => void;
  isCanceling: boolean;
  cancelError: string | null;
  cancelSuccess: boolean;
  onConfirmCancel: () => void;
}

export default function CancelSurveyModal({
  isOpen,
  onClose,
  cancelStep,
  setCancelStep,
  selectedReason,
  setSelectedReason,
  isCanceling,
  cancelError,
  cancelSuccess,
  onConfirmCancel
}: CancelSurveyModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { if (!isCanceling && !cancelSuccess) onClose(); }}
            className="absolute inset-0 bg-zinc-950/60 backdrop-blur-xs"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-zinc-100 flex flex-col z-10 max-h-[90vh]"
          >
            <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50">
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                {cancelSuccess ? 'Assinatura Cancelada' : 'Gerenciamento de Assinatura'}
              </h3>
              {!isCanceling && !cancelSuccess && (
                <button 
                  onClick={onClose}
                  className="p-1.5 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {cancelSuccess ? (
                <div className="text-center py-6 space-y-4">
                  <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto border border-emerald-100 text-emerald-500">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-zinc-900">Cancelamento Programado</h4>
                    <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                      Sua assinatura Pro foi programada para cancelamento. Você continuará com acesso aos recursos Pro até o encerramento do seu ciclo de cobrança.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full py-3.5 bg-zinc-900 text-white font-bold rounded-xl text-xs uppercase tracking-wider hover:bg-zinc-800 transition-all cursor-pointer"
                  >
                    Fechar
                  </button>
                </div>
              ) : cancelStep === 'survey' ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-black text-zinc-900">Poxa, você deseja nos deixar?</h4>
                    <p className="text-xs text-zinc-400 mt-1">Conta para a gente, qual é o principal motivo do cancelamento?</p>
                  </div>
                  
                  <div className="space-y-2 mt-4">
                    {[
                      { id: 'preco_alto', label: '💰 Achei o valor muito alto (caro)' },
                      { id: 'falta_recursos', label: '🧩 Faltam recursos no aplicativo' },
                      { id: 'nao_adaptei', label: '🎓 Não me adaptei ao uso da ferramenta' },
                      { id: 'baixo_faturamento', label: '📉 Meu faturamento está baixo no momento' },
                      { id: 'outro', label: '❓ Outro motivo' },
                    ].map((reason) => (
                      <button
                        key={reason.id}
                        type="button"
                        onClick={() => {
                          setSelectedReason(reason.label);
                          if (reason.id === 'preco_alto') {
                            setCancelStep('retention_price');
                          } else if (reason.id === 'falta_recursos') {
                            setCancelStep('retention_roadmap');
                          } else {
                            setCancelStep('confirm');
                          }
                        }}
                        className="w-full text-left p-3.5 border border-zinc-100 hover:border-blue-600 hover:bg-blue-50/20 rounded-2xl transition-all text-xs font-bold text-zinc-700 hover:text-blue-900 flex items-center justify-between group"
                      >
                        {reason.label}
                        <ChevronRight className="h-4 w-4 opacity-30 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : cancelStep === 'retention_price' ? (
                <div className="space-y-5 py-2">
                  <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center border border-amber-100 text-amber-500">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-zinc-900 leading-tight">Sabia que você pode economizar 18% no Pro?</h4>
                    <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                      Alterando seu plano para a modalidade <strong>Anual</strong>, o custo mensal cai para <strong>R$ 19,50</strong> (equivalente a R$ 195/ano parcelado em até 10x sem juros).
                    </p>
                  </div>
                  <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-4 text-[10px] text-zinc-500 leading-relaxed italic">
                    Dica: Se estiver com dificuldades para calcular seus custos operacionais ou seu valor-hora, nosso suporte pode te ajudar sem custo algum pelo WhatsApp!
                  </div>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl text-xs uppercase tracking-wider transition-all shadow-md cursor-pointer"
                    >
                      Quero Continuar Pro (Economizar 18%)
                    </button>
                    <button
                      type="button"
                      onClick={() => setCancelStep('confirm')}
                      className="w-full text-center text-xs font-bold text-zinc-400 hover:text-zinc-600 py-2 transition-colors cursor-pointer"
                    >
                      Ainda quero prosseguir com o cancelamento
                    </button>
                  </div>
                </div>
              ) : cancelStep === 'retention_roadmap' ? (
                <div className="space-y-5 py-2">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center border border-blue-100 text-blue-500">
                    <Info className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-zinc-900 leading-tight">Estamos preparando novidades incríveis!</h4>
                    <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                      Sua opinião é muito importante. Estamos preparando novas melhorias para as próximas semanas:
                    </p>
                    <ul className="list-disc pl-5 mt-2 text-xs text-zinc-500 space-y-1">
                      <li>Cobrança automática com chave PIX e link de pagamento.</li>
                      <li>Controle simplificado de entradas e saídas do caixa.</li>
                      <li>Metas de faturamento e resumo financeiro do mês.</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        window.open('https://wa.me/5511999999999?text=Olá! Estou usando o R-MAX e gostaria de sugerir uma nova funcionalidade...', '_blank');
                      }}
                      className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      Enviar Sugestão / Falar com Suporte
                    </button>
                    <button
                      type="button"
                      onClick={() => setCancelStep('confirm')}
                      className="w-full text-center text-xs font-bold text-zinc-400 hover:text-zinc-600 py-2 transition-colors cursor-pointer"
                    >
                      Ainda desejo cancelar a assinatura Pro
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5 py-2">
                  <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center border border-rose-100 text-rose-500">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-zinc-900">Confirmar cancelamento?</h4>
                    <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                      Ao confirmar, sua assinatura Pro será desativada ao final do período pago. Você perderá os recursos adicionais, como orçamentos ilimitados e o Radar de Mercado, voltando para o plano Grátis.
                    </p>
                  </div>
                  
                  {cancelError && (
                    <p className="text-xs text-red-500 font-bold bg-red-50 py-2.5 px-4 rounded-xl border border-red-100">
                      {cancelError}
                    </p>
                  )}

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={onConfirmCancel}
                      disabled={isCanceling}
                      className={`w-full py-3.5 bg-rose-600 hover:bg-rose-700 text-white font-black rounded-xl text-xs uppercase tracking-wider transition-all shadow-md cursor-pointer flex items-center justify-center gap-2 ${
                        isCanceling ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isCanceling ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Processando...
                        </>
                      ) : (
                        'Confirmar Cancelamento'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isCanceling}
                      className="w-full py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer"
                    >
                      Não, manter plano Pro
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
